import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { LaravelReport } from '../types/laravel';

/**
 * Full-panel report picker with search by name.
 *
 * Used when the host platform did NOT pass `current-report-id` —
 * the user needs to choose which dashboard to chat about.
 *
 * Display name is shown verbatim as the host platform provides it —
 * the previous heuristic that stripped a "Grupo do Master - " prefix
 * was removed because it silently hid meaningful naming and made
 * reports from different groups indistinguishable in the list.
 * The internal `token` is still the source of truth and is emitted
 * via the `pick` event.
 */
@customElement('bia-report-picker')
export class BiaReportPicker extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      background: var(--bia-background, #fafaf7);
    }
    .intro {
      padding: 22px 22px 14px;
    }
    .eyebrow {
      font-family: var(--bia-font-family, sans-serif);
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--bia-text-muted, #6b6b66);
      margin-bottom: 6px;
    }
    .title {
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-weight: 500;
      font-size: 22px;
      line-height: 1.2;
      letter-spacing: -0.018em;
      color: var(--bia-text, #0f0f0e);
    }
    .search {
      padding: 0 22px 12px;
    }
    .input-wrap {
      position: relative;
      display: block;
    }
    .search-icon {
      position: absolute;
      left: 12px;
      top: 0;
      bottom: 0;
      width: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      color: var(--bia-text-muted, #6b6b66);
    }
    .search-icon svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 14px 10px 36px;
      border: 1px solid var(--bia-border, #e8e5dc);
      border-radius: 12px;
      font-family: var(--bia-font-family, sans-serif);
      font-size: 13.5px;
      background: var(--bia-input-bg, #fff);
      color: var(--bia-text, #0f0f0e);
      outline: none;
      transition: border-color 160ms ease, box-shadow 160ms ease;
    }
    input::placeholder {
      color: var(--bia-text-muted, #6b6b66);
      opacity: 0.7;
    }
    input:focus {
      border-color: var(--bia-primary, #1a1a2e);
      box-shadow: 0 0 0 3px rgba(26, 26, 46, 0.06);
    }
    .list {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0 16px;
      scrollbar-width: thin;
      scrollbar-color: var(--bia-border, #e8e5dc) transparent;
    }
    .list::-webkit-scrollbar { width: 6px; }
    .list::-webkit-scrollbar-thumb {
      background: var(--bia-border, #e8e5dc);
      border-radius: 999px;
    }
    .item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 22px;
      cursor: pointer;
      font-family: var(--bia-font-family, sans-serif);
      font-size: 13.5px;
      font-weight: 500;
      color: var(--bia-text, #0f0f0e);
      border-bottom: 1px solid var(--bia-border-light, #f1efe7);
      transition: background 140ms ease, padding 140ms ease;
    }
    .item:hover {
      background: var(--bia-hover-bg, rgba(15, 15, 14, 0.04));
      padding-left: 26px;
    }
    .item:last-child { border-bottom: none; }
    .item .name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .item .arrow {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      color: var(--bia-text-muted, #6b6b66);
      opacity: 0;
      transform: translateX(-4px);
      transition: opacity 140ms ease, transform 140ms ease;
    }
    .item:hover .arrow {
      opacity: 1;
      transform: translateX(0);
    }
    .item .arrow svg {
      width: 100%;
      height: 100%;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .empty {
      padding: 32px 22px;
      text-align: center;
      color: var(--bia-text-muted, #6b6b66);
      font-family: var(--bia-font-family, sans-serif);
      font-size: 13px;
    }
    .count {
      padding: 8px 22px 4px;
      font-family: var(--bia-font-family, sans-serif);
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--bia-text-muted, #6b6b66);
    }
  `;

  @property({ type: Array }) reports: LaravelReport[] = [];

  @state() private query = '';

  private onSearch(e: Event): void {
    this.query = (e.target as HTMLInputElement).value;
  }

  private onPick(report: LaravelReport): void {
    this.dispatchEvent(
      new CustomEvent<LaravelReport>('pick', {
        detail: report,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private displayName(name: string): string {
    return name;
  }

  private filtered(): LaravelReport[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.reports;
    return this.reports.filter((r) => r.name.toLowerCase().includes(q));
  }

  render() {
    const list = this.filtered();
    const total = this.reports.length;
    const showing = list.length;
    const arrow = html`<svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;
    return html`
      <div class="intro">
        <div class="eyebrow">Onde quer começar?</div>
        <div class="title">Escolha um relatório.</div>
      </div>
      <div class="search">
        <div class="input-wrap">
          <span class="search-icon">
            <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          </span>
          <input
            type="search"
            placeholder="Buscar por nome..."
            .value=${this.query}
            @input=${this.onSearch}
            autofocus
          />
        </div>
      </div>
      ${showing > 0
        ? html`<div class="count">
            ${showing === total ? `${total} disponíveis` : `${showing} de ${total}`}
          </div>`
        : null}
      <div class="list">
        ${list.length === 0
          ? html`<div class="empty">Nenhum relatório encontrado</div>`
          : list.map(
              (r) => html`
                <div class="item" @click=${() => this.onPick(r)}>
                  <span class="name">${this.displayName(r.name)}</span>
                  <span class="arrow">${arrow}</span>
                </div>
              `,
            )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bia-report-picker': BiaReportPicker;
  }
}
